import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchCandidates, createCandidate, deleteCandidate, clearCandidateError } from "../slice/candidateSlice";
import { fetchClients } from "../slice/clientSlice";
import { useDialog } from "../components/ConfirmDialog";
import { viewFileInNewTab, downloadFile } from "../utils/viewFile";
import { addedByLabel, formatDateTime } from "../utils/formatters";
import { EyeIcon, DownloadIcon } from "../components/icons";
import CandidateFilters from "../components/CandidateFilters";
import Pagination from "../components/Pagination";
import api from "../api/axiosInstance";
// Shares the page, table, button and slide-in panel styles with the
// Clients page so both look the same; Dashboard.css adds only what's
// specific to candidates (column widths, resume cell, file picker).
import "../css/ClientList.css";
import "../css/Dashboard.css";

const EMPTY_FILTERS = { startDate: "", endDate: "", addedBy: "", technology: "", clientId: "" };
const EMPTY_CANDIDATE = { name: "", email: "", phone: "" };
const PAGE_SIZE = 10;

// Two-letter monogram for the candidate column.
const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

// "10/2/2026, 10:54:40 AM" -> date on one line, time on the next.
const splitDateTime = (value) => {
  const text = formatDateTime(value) || "";
  const i = text.indexOf(", ");
  return i === -1 ? [text, ""] : [text.slice(0, i), text.slice(i + 2)];
};

export default function Dashboard() {
  const [newCandidate, setNewCandidate] = useState(EMPTY_CANDIDATE);
  const [resumeFile, setResumeFile] = useState(null);
  const [viewingResumeId, setViewingResumeId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const fileInputRef = useRef(null);

  // draftFilters is what the form controls are bound to; appliedFilters is
  // what was last actually sent to the server — kept separate so editing a
  // dropdown doesn't refetch on every keystroke, only on "Apply filters".
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);

  const { user } = useSelector((state) => state.auth);
  const { candidates, pagination, status, error } = useSelector((state) => state.candidates);
  const { clients } = useSelector((state) => state.clients);
  const dispatch = useDispatch();
  const loading = status === "loading";
  const { dialogElement, notify, confirm } = useDialog();
  const isAdmin = user?.role === "ADMIN";

  useEffect(() => {
    dispatch(fetchCandidates({ ...appliedFilters, page, limit: PAGE_SIZE }));
    dispatch(fetchClients());
    return () => dispatch(clearCandidateError());
    // appliedFilters intentionally excluded here — the Apply/Reset
    // handlers below dispatch fetchCandidates themselves with the exact
    // filters just applied, so re-running on every appliedFilters change
    // would just be a redundant second fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, page]);

  // The "Added by" filter is admin-only (see CandidateFilters), so only
  // admins need this list.
  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/admin/users")
      .then((res) => setUsers(res.data.users))
      .catch(() => {});
  }, [isAdmin]);

  // Esc closes the Add candidate panel (unless a save is in progress).
  useEffect(() => {
    if (!formOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) closeForm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formOpen, loading]);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters(draftFilters);
    dispatch(fetchCandidates({ ...draftFilters, page: 1, limit: PAGE_SIZE }));
  };

  const resetFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
    dispatch(fetchCandidates({ page: 1, limit: PAGE_SIZE }));
  };

  // Uses the shared confirm dialog instead of inline Confirm/Cancel
  // buttons, so the Actions column keeps a fixed width.
  const handleDelete = async (c) => {
    const ok = await confirm(
      `Delete ${c.name}? This removes the candidate and their resume history, and cannot be undone.`,
      { title: "Delete candidate", confirmLabel: "Delete" }
    );
    if (!ok) return;
    await dispatch(deleteCandidate(c._id));
  };

  const isOwner = (candidate) => {
    const ownerId =
      typeof candidate.createdBy === "object" ? candidate.createdBy?._id : candidate.createdBy;
    const currentUserId = user?.id || user?._id;
    return ownerId === currentUserId;
  };

  // Opens the candidate's current master resume in a new tab.
  const handleViewMasterResume = async (candidate) => {
    const master = candidate.latestMasterResume;
    if (!master) return;

    setViewingResumeId(candidate._id);
    await viewFileInNewTab(`/candidate/${candidate._id}/resume-versions/${master.id}/download`, {
      onError: () => notify("Could not open this resume. Please try again.", { title: "View resume failed" }),
    });
    setViewingResumeId(null);
  };

  // Saves the master resume straight to disk.
  const handleDownloadMasterResume = async (candidate) => {
    const master = candidate.latestMasterResume;
    if (!master) return;

    await downloadFile(
      `/candidate/${candidate._id}/resume-versions/${master.id}/download`,
      master.fileName,
      { onError: () => notify("Could not download this resume. Please try again.", { title: "Download failed" }) }
    );
  };

  const openForm = () => {
    dispatch(clearCandidateError());
    setNewCandidate(EMPTY_CANDIDATE);
    setResumeFile(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setNewCandidate(EMPTY_CANDIDATE);
    setResumeFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    dispatch(clearCandidateError());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newCandidate.name.trim() || !newCandidate.email.trim()) return;

    const formData = new FormData();
    formData.append("name", newCandidate.name);
    formData.append("email", newCandidate.email);
    formData.append("phone", newCandidate.phone);
    if (resumeFile) formData.append("resume", resumeFile);

    const result = await dispatch(createCandidate(formData));
    if (createCandidate.fulfilled.match(result)) {
      closeForm();
    }
    // on failure, the slice sets `error`, which shows inside the panel
  };

  const setField = (key) => (e) => setNewCandidate((p) => ({ ...p, [key]: e.target.value }));

  const renderTable = () => {
    if (loading && candidates.length === 0 && !formOpen) {
      return <div className="cl-empty">Loading candidates…</div>;
    }
    if (candidates.length === 0) {
      return (
        <div className="cl-empty">
          <strong>No candidates match these filters.</strong>
          <span>{isAdmin ? "Try clearing the filters above." : "Clear the filters, or add a new candidate."}</span>
        </div>
      );
    }

    return (
      <>
        <div className="cl-table-wrap">
          <table className={`cl-table ${isAdmin ? "cd-table-admin" : "cd-table-user"}`}>
            <colgroup>
              <col className="cd-col-candidate" />
              <col className="cd-col-phone" />
              <col className="cd-col-resume" />
              {isAdmin && <col className="cd-col-by" />}
              <col className="cd-col-date" />
              <col className="cd-col-actions" />
            </colgroup>
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Phone</th>
                <th>Master resume</th>
                {isAdmin && <th>Added by</th>}
                <th>Added on</th>
                <th className="cl-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => {
                const [date, time] = splitDateTime(c.createdAt);
                const master = c.latestMasterResume;
                const owner = isOwner(c);
                return (
                  <tr key={c._id}>
                    <td>
                      <div className="cl-name">
                        <span className="cl-mono">{initials(c.name)}</span>
                        <div className="cd-person">
                          <Link to={`/dashboard/candidates/${c._id}`} className="cd-person-name" title={c.name}>
                            {c.name}
                          </Link>
                          <span className="cd-person-email" title={c.email}>
                            {c.email}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="cd-nowrap">{c.phone || <span className="cl-dash">—</span>}</td>

                    <td>
                      {master ? (
                        <div className="cd-resume">
                          <span className="cd-version" title={master.fileName}>
                            v{master.version}
                          </span>
                          <button
                            type="button"
                            className="cl-btn cl-btn-row cd-icon-btn"
                            onClick={() => handleViewMasterResume(c)}
                            disabled={viewingResumeId === c._id}
                            title={`View ${master.fileName}`}
                          >
                            <EyeIcon />
                            {viewingResumeId === c._id ? "Opening…" : "View"}
                          </button>
                          <button
                            type="button"
                            className="cl-btn cl-btn-row cd-icon-btn"
                            onClick={() => handleDownloadMasterResume(c)}
                            title={`Download ${master.fileName}`}
                          >
                            <DownloadIcon />
                            Download
                          </button>
                        </div>
                      ) : (
                        <span className="cd-no-resume">No resume yet</span>
                      )}
                    </td>

                    {isAdmin && (
                      <td className="cd-truncate" title={addedByLabel(c.createdBy)}>
                        {addedByLabel(c.createdBy)}
                      </td>
                    )}

                    <td>
                      <div className="cl-date">
                        <span>{date}</span>
                        {time && <span className="cl-time">{time}</span>}
                      </div>
                    </td>

                    <td className="cl-actions">
                      <Link to={`/dashboard/candidates/${c._id}`} className="cl-btn cl-btn-row">
                        View
                      </Link>
                      {owner && (
                        <Link to={`/dashboard/candidates/${c._id}/edit`} className="cl-btn cl-btn-row">
                          Edit
                        </Link>
                      )}
                      {owner && (
                        <button type="button" className="cl-btn cl-btn-row cl-btn-danger" onClick={() => handleDelete(c)}>
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="cl-pagination">
          <Pagination pagination={pagination} onPageChange={setPage} />
        </div>
      </>
    );
  };

  return (
    <div className="cl-page">
      <header className="cl-header">
        <div>
          <h2>Candidates</h2>
          <p>{isAdmin ? "Every candidate added across your teams." : "Candidates you're preparing and submitting."}</p>
        </div>
        {!isAdmin && (
          <div className="cd-header-actions">
            <Link to="/dashboard/optimizations/new" className="cl-btn cl-btn-secondary">
              Start optimization
            </Link>
            <button type="button" className="cl-btn cl-btn-add" onClick={openForm}>
              <span aria-hidden="true">+</span> Add candidate
            </button>
          </div>
        )}
      </header>

      {error && !formOpen && (
        <div className="cl-error" role="alert">
          {error}
        </div>
      )}

      <div className="cl-filters">
        <CandidateFilters
          value={draftFilters}
          onChange={setDraftFilters}
          onApply={applyFilters}
          onReset={resetFilters}
          showAddedByFilter={isAdmin}
          users={users}
          clients={clients}
        />
      </div>

      <section className="cl-card">
        <div className="cl-card-head">
          <h2>{isAdmin ? "All candidates" : "Your candidates"}</h2>
        </div>
        {renderTable()}
      </section>

      {!isAdmin && formOpen && (
        <div className="cl-drawer-root">
          <div className="cl-backdrop" onClick={() => !loading && closeForm()} />
          <form
            className="cl-drawer"
            onSubmit={handleSubmit}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cd-drawer-title"
          >
            <div className="cl-drawer-head">
              <div>
                <h2 id="cd-drawer-title">Add candidate</h2>
                <p>Start with the basics. Experience, education and skills can be added from Edit later.</p>
              </div>
              <button
                type="button"
                className="cl-btn cl-btn-close"
                onClick={closeForm}
                disabled={loading}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="cl-drawer-body">
              <div className="cl-field">
                <label htmlFor="cd-name">
                  Full name <span className="cl-req">*</span>
                </label>
                <input
                  id="cd-name"
                  type="text"
                  value={newCandidate.name}
                  onChange={setField("name")}
                  placeholder="e.g. John Smith"
                  autoFocus
                  required
                />
              </div>

              <div className="cl-field">
                <label htmlFor="cd-email">
                  Email <span className="cl-req">*</span>
                </label>
                <input
                  id="cd-email"
                  type="email"
                  value={newCandidate.email}
                  onChange={setField("email")}
                  placeholder="e.g. john.smith@example.com"
                  required
                />
              </div>

              <div className="cl-field">
                <label htmlFor="cd-phone">Phone</label>
                <input
                  id="cd-phone"
                  type="tel"
                  value={newCandidate.phone}
                  onChange={setField("phone")}
                  placeholder="e.g. +1 555 123 4567"
                />
              </div>

              <div className="cl-field">
                <span className="cd-label">Master resume</span>
                <label className={`cd-file${resumeFile ? " has-file" : ""}`}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".doc,.docx"
                    onChange={(e) => setResumeFile(e.target.files[0] || null)}
                  />
                  <span className="cd-file-main">{resumeFile ? resumeFile.name : "Choose a file"}</span>
                  <span className="cd-file-sub">
                    {resumeFile ? "Click to choose a different file" : "DOC or DOCX. Optional, you can add it later."}
                  </span>
                </label>
              </div>

              {error && (
                <div className="cl-form-error" role="alert">
                  {error}
                </div>
              )}
            </div>

            <div className="cl-drawer-foot">
              <button type="button" className="cl-btn cl-btn-secondary" onClick={closeForm} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="cl-btn cl-btn-primary" disabled={loading}>
                {loading ? "Saving…" : "Add candidate"}
              </button>
            </div>
          </form>
        </div>
      )}

      {dialogElement}
    </div>
  );
}
